import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { getDocs, collection, doc } from 'firebase/firestore';
import { db } from './firebase';
import { getStatsCollectionRoot } from './utils/teamStatsConfig';
import useDocumentMeta from './useDocumentMeta';
import useJsonLd from './useJsonLd';
import ParentTestimonialsCarousel from './ParentTestimonialsCarousel';
import SponsorWall from './components/SponsorWall';

import logo from './assets/images/gladiators-hero.png';
import placeholder from './assets/images/placeholder.jpg';
import explodingGlassVideo from './assets/videos/Exploding_Glass_Reveals_City_and_Team.mp4';
import aboutStoryVideo from './assets/videos/16x9_GlassCity Hockey.mp4';
import heroImage from './assets/images/city-skyline-hero.png';
import { Link } from 'react-router-dom';
import useCoachesCatalog from './hooks/useCoachesCatalog';
import { coachRoleLine } from './utils/coachesCatalog';

// Official Team Colors - Updated to match SplitScroll.css
const TEAM_COLORS = {
  vegasGold: '#B4975A',
  sanJoseTeal: '#006D75',
  gladiatorGrey: '#a7a7b2',
  black: '#000000',
  tealGlow: 'rgba(0, 109, 117, 0.6)',
  goldGlow: 'rgba(180, 151, 90, 0.6)'
};

// Lazy-loading image component
const LazyImage = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);

  const imageStyles = {
    width: 200,
    height: 200,
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
    margin: '0 auto',
    border: `4px solid ${TEAM_COLORS.sanJoseTeal}`,
    boxShadow: `0 0 20px ${TEAM_COLORS.tealGlow}, 0 0 40px ${TEAM_COLORS.tealGlow}`,
    transition: 'all 0.3s ease'
  };

  return (
    <div>
      {!loaded && (
        <img src={placeholder} alt="loading" style={{ ...imageStyles, opacity: 0.5 }} />
      )}
      <img 
        src={src} 
        alt={alt} 
        onLoad={() => setLoaded(true)} 
        loading="lazy" 
        style={{ 
          ...imageStyles, 
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'scale(1)' : 'scale(0.95)'
        }} 
      />
    </div>
  );
};

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// OPTION 1: Full-Screen Video Hero Background with Hero Image Reveal - MOBILE OPTIMIZED
const VideoHeroBackground = ({ videoSrc, heroImageSrc, children, mobileOpacity = 0.4, desktopOpacity = 0.8 }) => {
  const videoRef = useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(err => console.log('Autoplay prevented:', err));
    }
  }, []);

  const handleVideoEnd = () => {
    setVideoEnded(true);
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', backgroundColor: '#0a0a0a' }}>
      {/* Video Background */}
      <video
        ref={videoRef}
        src={videoSrc}
        autoPlay
        muted
        playsInline
        preload={isMobile ? "metadata" : "auto"}
        onLoadedData={() => setIsVideoLoaded(true)}
        onEnded={handleVideoEnd}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '100%' : 'auto',
          height: isMobile ? 'auto' : '100%',
          minWidth: isMobile ? '100%' : '100%',
          minHeight: isMobile ? '100%' : '100%',
          maxWidth: isMobile ? '100%' : 'none',
          zIndex: 0,
          objectFit: isMobile ? 'contain' : 'cover',
          objectPosition: 'center center',
          opacity: isVideoLoaded && !videoEnded ? (isMobile ? mobileOpacity : desktopOpacity) : 0,
          transition: 'opacity 1.5s ease-in-out'
        }}
      />
      
      {/* Hero Image - Appears after video ends */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ 
          opacity: videoEnded ? 1 : 0
        }}
        transition={{ 
          duration: 2,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          pointerEvents: videoEnded ? 'auto' : 'none'
        }}
      >
        <img 
          src={heroImageSrc}
          alt="Glass City Gladiators - City Skyline"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center'
          }}
        />
      </motion.div>

      {/* Scroll Indicator - Appears with hero image */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ 
          opacity: videoEnded ? 1 : 0
        }}
        transition={{ 
          duration: 1,
          delay: 2
        }}
        style={{
          position: 'absolute',
          bottom: 30,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
          textAlign: 'center'
        }}
      >
        <div style={{
          fontSize: '0.9rem',
          color: TEAM_COLORS.sanJoseTeal,
          fontWeight: 600,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 10,
          textShadow: '0 2px 10px rgba(0, 0, 0, 0.8)'
        }}>
          Scroll to Explore
        </div>
        <div style={{
          fontSize: '2rem',
          color: TEAM_COLORS.sanJoseTeal,
          animation: 'bounce 2s ease-in-out infinite'
        }}>
          ↓
        </div>
      </motion.div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes bounce {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-10px);
            }
          }
        `}
      </style>
      
      {/* Content overlay */}
      <div style={{ position: 'relative', zIndex: 3 }}>
        {children}
      </div>
    </div>
  );
};

// OPTION 2: Scroll-Triggered Video Section - MOBILE OPTIMIZED
const ScrollTriggeredVideo = ({ videoSrc }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasPlayed) {
          setIsVisible(true);
          if (videoRef.current) {
            videoRef.current.play().catch(err => console.log('Play prevented:', err));
            setHasPlayed(true);
          }
        }
      },
      { threshold: isMobile ? 0.3 : 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasPlayed, isMobile]);

  const handleVideoEnd = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(err => console.log('Replay prevented:', err));
    }
  };

  return (
    <div 
      ref={sectionRef} 
      style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: isMobile ? '40px 15px' : '60px 20px'
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ 
          opacity: isVisible ? 1 : 0,
          scale: isVisible ? 1 : 0.9
        }}
        transition={{ duration: 0.8 }}
        style={{
          width: '100%',
          maxWidth: isMobile ? '100%' : 800,
          margin: '0 auto',
          borderRadius: isMobile ? 8 : 16,
          overflow: 'hidden',
          boxShadow: `0 20px 60px ${TEAM_COLORS.tealGlow}`,
          border: isMobile ? `1px solid ${TEAM_COLORS.sanJoseTeal}` : `2px solid ${TEAM_COLORS.sanJoseTeal}`
        }}
      >
        <video
          ref={videoRef}
          src={videoSrc}
          muted
          playsInline
          preload={isMobile ? "metadata" : "auto"}
          onEnded={handleVideoEnd}
          style={{
            width: '100%',
            display: 'block'
          }}
        />
      </motion.div>
    </div>
  );
};

// Interactive Call-to-Action Button
const InteractiveCTA = ({ onClick }) => {
  const isMobile = useIsMobile();
  
  return (
    <motion.button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobile ? 10 : 15,
        padding: isMobile ? '15px 30px' : '20px 40px',
        background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`,
        color: '#0a0a0a',
        border: 'none',
        borderRadius: 8,
        fontSize: isMobile ? '1rem' : '1.2rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: isMobile ? 1 : 2,
        cursor: 'pointer',
        boxShadow: `0 10px 30px ${TEAM_COLORS.tealGlow}`,
        transition: 'all 0.3s ease',
        touchAction: 'manipulation'
      }}
      whileHover={{ 
        scale: 1.05,
        boxShadow: `0 15px 40px ${TEAM_COLORS.tealGlow}`
      }}
      whileTap={{ scale: 0.98 }}
    >
      <span>Learn More</span>
      <span style={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>→</span>
    </motion.button>
  );
};

const TEAM_ID = 'glass-city-gladiators-12u';

// Main SplitScroll Component
const SplitScroll = () => {
  const isMobile = useIsMobile();
  const [recentGames, setRecentGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(true);
  const [tournaments, setTournaments] = useState([]);
  const [loadingTournaments, setLoadingTournaments] = useState(true);

  const { coaches } = useCoachesCatalog();

  useDocumentMeta({
    title: 'Glass City Gladiators | Nonprofit Youth Travel Hockey in Toledo',
    description: 'Join the Glass City Gladiators for affordable, family-first 12U travel hockey in the Greater Toledo area. Learn about our mission, coaches, and how to sign up today!',
    canonical: 'https://gcghockey.com/',
    ogImage: 'https://gcghockey.com/assets/og-home.png',
    ogType: 'website'
  });

  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'SportsTeam',
    name: 'Glass City Gladiators',
    url: 'https://gcghockey.com',
    sport: 'IceHockey',
    description: 'Nonprofit youth travel hockey program in the Greater Toledo area.',
    member: coaches.map((c) => ({
      '@type': 'Person',
      name: c.name,
      role: c.role || c.tagline || 'Coach',
    })),
  });

  useEffect(() => {
    const gameHistoryRef = collection(doc(db, getStatsCollectionRoot(), TEAM_ID), 'gameHistory');
    getDocs(gameHistoryRef)
      .then(snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const shown = list
          .filter(row => row.showOnHome !== false)
          .sort((a, b) => (new Date(b.endedAt || 0) - new Date(a.endedAt || 0)))
          .slice(0, 5);
        setRecentGames(shown);
      })
      .catch(err => {
        console.error('Error loading recent games:', err);
      })
      .finally(() => setLoadingGames(false));
  }, []);

  useEffect(() => {
    const ts = new Date().getTime();
    const tsvUrl = `https://docs.google.com/spreadsheets/d/e/2PACX-1vS068-sX2VEWrswmUdXiYtbVHRVwL_3OtOJE96GLWgWlYqeT5TSrVEzsOUXat72jt_2-oDd-L96UeQF/pub?gid=0&single=true&output=tsv&t=${ts}`;
    fetch(tsvUrl)
      .then(res => res.text())
      .then(tsv => {
        const [headerLine, ...lines] = tsv.trim().split('\n');
        const headers = headerLine.split('\t').map(h => h.trim());
        const idx = h => headers.indexOf(h);
        const data = lines.map(line => {
          const cols = line.split('\t');
          while (cols.length < headers.length) cols.push('');
          return {
            name: cols[idx('Tournament Name')] || '',
            location: cols[idx('Location')] || '',
            dates: cols[idx('Dates')] || '',
            organizer: cols[idx('Organizer')] || ''
          };
        });
        setTournaments(data);
      })
      .catch(() => {})
      .finally(() => setLoadingTournaments(false));
  }, []);


  return (
    <div style={{
      background: '#0a0a0a',
      color: '#f0f8ff',
      fontFamily: "'Helvetica Neue', Arial, sans-serif",
      minHeight: '100vh',
      position: 'relative'
    }}>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0.03,
        background: `
          repeating-linear-gradient(90deg, transparent, transparent 2px, ${TEAM_COLORS.sanJoseTeal} 2px, ${TEAM_COLORS.sanJoseTeal} 4px),
          repeating-linear-gradient(0deg, transparent, transparent 2px, ${TEAM_COLORS.sanJoseTeal} 2px, ${TEAM_COLORS.sanJoseTeal} 4px)
        `,
        backgroundSize: '100px 100px',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <VideoHeroBackground 
          videoSrc={explodingGlassVideo}
          heroImageSrc={heroImage}
          mobileOpacity={0.7}
          desktopOpacity={0.95}
        >
          <div style={{ 
            minHeight: '100vh', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: isMobile ? '30px 15px' : '40px 20px'
          }}>
            {/* Clean video display - hero image appears after video ends */}
          </div>
        </VideoHeroBackground>

        {/* Video Hero - More Than a Team */}
        <section style={{ position: 'relative', minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', overflow: 'hidden', background: '#0a0a0a' }}>
          <div style={{ position: 'absolute', inset: 0 }}>
            <video
              src={aboutStoryVideo}
              autoPlay
              muted
              loop
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              aria-hidden="true"
            />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.75) 100%)' }} />
          </div>
          <div style={{ position: 'relative', zIndex: 2, padding: isMobile ? '40px 20px' : '40px 24px', maxWidth: 900, margin: '0 auto' }}>
            <motion.h1
              style={{
                fontSize: isMobile ? 'clamp(2rem, 6vw, 2.8rem)' : 'clamp(2rem, 6vw, 3.5rem)',
                fontWeight: 800,
                lineHeight: 1.15,
                margin: '0 0 20px',
                textTransform: 'uppercase',
                letterSpacing: 2,
                background: `linear-gradient(135deg, #fff 0%, ${TEAM_COLORS.vegasGold} 50%, ${TEAM_COLORS.sanJoseTeal} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
              initial={{ opacity: 0, y: -24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
            >
              NO POLITICS. NO EGOS. JUST HOCKEY.
            </motion.h1>
            <motion.p
              style={{
                fontSize: isMobile ? 'clamp(1rem, 2.2vw, 1.2rem)' : 'clamp(1rem, 2.2vw, 1.35rem)',
                lineHeight: 1.6,
                color: 'rgba(255, 255, 255, 0.95)',
                margin: '0 0 32px',
                maxWidth: 600,
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 }}
            >
              We are fixing what's broken in travel hockey. If you want elite AA development without the locker room drama, you are in the right place.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3 }}
            >
              <Link
                to="/tryouts"
                style={{
                  display: 'inline-block',
                  padding: '14px 32px',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  color: '#0a0a0a',
                  background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`,
                  borderRadius: 8,
                  textDecoration: 'none',
                  boxShadow: `0 8px 24px ${TEAM_COLORS.tealGlow}`,
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease'
                }}
                onMouseOver={e => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = `0 12px 32px ${TEAM_COLORS.goldGlow}`;
                }}
                onMouseOut={e => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 8px 24px ${TEAM_COLORS.tealGlow}`;
                }}
              >
                See what we're about
              </Link>
            </motion.div>
          </div>
        </section>

        {/* AN AMAZING EXPERIENCE: PERIOD. */}
        <section style={{ padding: isMobile ? '50px 15px' : '80px 20px', background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)', position: 'relative' }}>
          <motion.h2
            style={{ textAlign: 'center', fontSize: isMobile ? 'clamp(1.8rem, 7vw, 2.5rem)' : 'clamp(2rem, 5vw, 3.5rem)', background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: isMobile ? 28 : 36, textTransform: 'uppercase', letterSpacing: isMobile ? 2 : 3, fontWeight: 700 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            AN AMAZING EXPERIENCE: PERIOD.
          </motion.h2>
          <motion.p
            style={{ maxWidth: 760, margin: '0 auto', fontSize: isMobile ? '1.05rem' : '1.2rem', lineHeight: 1.85, color: '#e8eaed', textAlign: 'center' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Let's be real. Your kid may or may not make it to the NHL. But in this program, they will 100 percent fall in love with the game. We are here to make hockey an incredible experience for the players and the families. We build better athletes by building a better culture. We win because we develop. We develop because we care. It is that simple.
          </motion.p>
        </section>

        {/* THE GLADIATOR WAY */}
        <section style={{ padding: isMobile ? '50px 15px' : '80px 20px', background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '80%', height: 2, background: `linear-gradient(90deg, transparent, ${TEAM_COLORS.sanJoseTeal}, transparent)` }} />
          <motion.h2
            style={{ textAlign: 'center', fontSize: isMobile ? 'clamp(1.8rem, 7vw, 2.5rem)' : 'clamp(2rem, 5vw, 3.5rem)', background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: isMobile ? 32 : 48, textTransform: 'uppercase', letterSpacing: isMobile ? 2 : 3, fontWeight: 700 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            THE GLADIATOR WAY
          </motion.h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: isMobile ? 20 : 28, maxWidth: 1200, margin: '0 auto' }}>
            {[
              { title: 'AA Intensity', desc: 'We compete at the highest levels while leaving politics at the door.' },
              { title: 'Development First', desc: 'We don\'t just play games. We build skill sets. Our focus is making every player on the roster better every single day.' },
              { title: 'A Winning Attitude', desc: 'Winning is a byproduct of work ethic, resilience, and grit.' },
              { title: 'Family Culture', desc: 'Ask our current families. This is a program where parents actually enjoy hanging out together.' }
            ].map((item, i) => (
              <motion.div
                key={i}
                style={{ background: 'linear-gradient(135deg, #1a1a1a, #0a0a0a)', padding: isMobile ? 22 : 28, border: `2px solid ${TEAM_COLORS.sanJoseTeal}`, borderRadius: 12, boxShadow: `0 10px 30px ${TEAM_COLORS.tealGlow}`, transition: 'all 0.3s ease' }}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ transform: 'translateY(-5px)', borderColor: TEAM_COLORS.vegasGold, boxShadow: `0 15px 40px ${TEAM_COLORS.goldGlow}` }}
              >
                <strong style={{ color: TEAM_COLORS.sanJoseTeal, fontSize: '1.15rem', display: 'block', marginBottom: 6 }}>{item.title}</strong>
                <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', lineHeight: 1.6 }}>{item.desc}</span>
              </motion.div>
            ))}
          </div>
        </section>

        <section style={{ 
          padding: isMobile ? '50px 15px' : '80px 20px',
          background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            height: 2,
            background: `linear-gradient(90deg, transparent, ${TEAM_COLORS.sanJoseTeal}, transparent)`
          }} />
          
          <motion.h2 
            style={{ 
              textAlign: 'center', 
              fontSize: isMobile ? 'clamp(1.8rem, 7vw, 2.5rem)' : 'clamp(2rem, 5vw, 3.5rem)', 
              background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: isMobile ? 40 : 60, 
              textTransform: 'uppercase', 
              letterSpacing: isMobile ? 2 : 3,
              fontWeight: 700
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Recent Games
          </motion.h2>

          {loadingGames ? (
            <div style={{ textAlign: 'center', color: TEAM_COLORS.sanJoseTeal, fontSize: isMobile ? '1rem' : '1.2rem' }}>
              Loading games...
            </div>
          ) : recentGames.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: isMobile ? '20px' : '30px',
              maxWidth: 1200,
              margin: '0 auto'
            }}>
              {recentGames.map((game, i) => {
                const details = game.details || {};
                const score = (game.gameData || {}).score || {};
                const us = score.us ?? 0;
                const them = score.them ?? 0;
                const isWin = us > them;
                const dateStr = details.date ? new Date(details.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                return (
                  <motion.div
                    key={game.id}
                    style={{
                      background: 'linear-gradient(135deg, #1a1a1a, #0a0a0a)',
                      borderRadius: isMobile ? 10 : 12,
                      padding: isMobile ? 20 : 25,
                      border: isMobile ? `1px solid ${TEAM_COLORS.sanJoseTeal}` : `2px solid ${TEAM_COLORS.sanJoseTeal}`,
                      boxShadow: `0 8px 25px ${TEAM_COLORS.tealGlow}`,
                      transition: 'all 0.3s ease'
                    }}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    whileHover={{
                      scale: 1.02,
                      borderColor: TEAM_COLORS.vegasGold,
                      boxShadow: `0 12px 35px ${TEAM_COLORS.goldGlow}`
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <span style={{
                        fontSize: isMobile ? '0.85rem' : '0.9rem',
                        fontWeight: 700,
                        padding: '4px 8px',
                        borderRadius: 6,
                        background: isWin ? 'rgba(76, 175, 80, 0.25)' : 'rgba(158, 158, 158, 0.25)',
                        color: isWin ? '#81c784' : TEAM_COLORS.gladiatorGrey
                      }}>
                        {isWin ? 'W' : 'L'}
                      </span>
                      <span style={{ fontSize: isMobile ? '1.2rem' : '1.3rem', color: TEAM_COLORS.sanJoseTeal, fontWeight: 700 }}>
                        {dateStr}
                      </span>
                    </div>
                    <div style={{
                      fontSize: isMobile ? '1.1rem' : '1.2rem',
                      color: TEAM_COLORS.vegasGold,
                      fontWeight: 600,
                      marginBottom: 10
                    }}>
                      vs {details.opponent || 'Opponent'}
                    </div>
                    <div style={{
                      fontSize: isMobile ? '0.85rem' : '0.9rem',
                      color: TEAM_COLORS.gladiatorGrey,
                      marginBottom: 10
                    }}>
                      {details.location || '—'}
                    </div>
                    <div style={{
                      fontSize: isMobile ? '1.1rem' : '1.25rem',
                      color: '#f0f8ff',
                      fontWeight: 700,
                      marginTop: 10,
                      paddingTop: 10,
                      borderTop: `1px solid ${TEAM_COLORS.tealGlow}`
                    }}>
                      Gladiators {us} – {details.opponent || 'Opponent'} {them}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#f0f8ff', fontSize: isMobile ? '1rem' : '1.2rem' }}>
              No recent games to display.
            </div>
          )}
        </section>

        <section style={{ 
          padding: isMobile ? '50px 15px' : '80px 20px',
          background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            height: 2,
            background: `linear-gradient(90deg, transparent, ${TEAM_COLORS.sanJoseTeal}, transparent)`
          }} />
          
          <motion.h2 
            style={{ 
              textAlign: 'center', 
              fontSize: isMobile ? 'clamp(1.8rem, 7vw, 2.5rem)' : 'clamp(2rem, 5vw, 3.5rem)', 
              background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: isMobile ? 35 : 50, 
              textTransform: 'uppercase', 
              letterSpacing: isMobile ? 2 : 3,
              fontWeight: 700
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What Parents Are Saying
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ParentTestimonialsCarousel />
          </motion.div>

          <div style={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            height: 2,
            background: `linear-gradient(90deg, transparent, ${TEAM_COLORS.vegasGold}, transparent)`
          }} />
        </section>

        <section style={{ 
          textAlign: 'center', 
          padding: isMobile ? '50px 15px' : '80px 20px',
          background: '#0a0a0a'
        }}>
          <motion.h2 
            style={{ 
              fontSize: isMobile ? 'clamp(1.8rem, 7vw, 2.5rem)' : 'clamp(2rem, 5vw, 3.5rem)', 
              background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: isMobile ? 40 : 60, 
              textTransform: 'uppercase', 
              letterSpacing: isMobile ? 2 : 3,
              fontWeight: 700
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Leadership Team
          </motion.h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: isMobile ? '30px' : '40px 60px',
            maxWidth: '1000px',
            margin: '0 auto',
            justifyItems: 'center'
          }}>
            {coaches.map((c, j) => (
              <motion.div 
                key={c.id || c.name || j} 
                style={{ 
                  background: 'linear-gradient(135deg, #1a1a1a, #0a0a0a)',
                  borderRadius: isMobile ? 12 : 16, 
                  width: isMobile ? '100%' : 280,
                  maxWidth: 280, 
                  padding: isMobile ? 30 : 35, 
                  textAlign: 'center', 
                  border: isMobile ? `1px solid ${TEAM_COLORS.sanJoseTeal}` : `2px solid ${TEAM_COLORS.sanJoseTeal}`,
                  boxShadow: `0 10px 30px ${TEAM_COLORS.tealGlow}`,
                  transition: 'all 0.3s ease'
                }}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: j * 0.15 }}
                whileHover={{ 
                  scale: 1.05,
                  borderColor: TEAM_COLORS.vegasGold,
                  boxShadow: `0 15px 40px ${TEAM_COLORS.goldGlow}`
                }}
              >
                <LazyImage src={c.photoUrl || placeholder} alt={c.name} />
                <div style={{ 
                  fontSize: isMobile ? '1.3rem' : '1.4rem', 
                  background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  margin: '15px 0 8px',
                  fontWeight: 700,
                  letterSpacing: 1
                }}>
                  {c.name}
                </div>
                <div style={{ 
                  fontSize: isMobile ? '0.95rem' : '1rem', 
                  color: TEAM_COLORS.sanJoseTeal,
                  fontWeight: 500,
                  letterSpacing: 0.5
                }}>
                  {coachRoleLine(c)}
                </div>
                {c.bio ? (
                  <div style={{
                    marginTop: 12,
                    fontSize: isMobile ? '0.85rem' : '0.9rem',
                    color: TEAM_COLORS.gladiatorGrey,
                    lineHeight: 1.45,
                    textAlign: 'center',
                  }}>
                    {c.bio}
                  </div>
                ) : null}
              </motion.div>
            ))}
          </div>
        </section>

        <SponsorWall />

        {/* Tournament schedule */}
        <section style={{ padding: isMobile ? '50px 15px' : '80px 20px', background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '80%', height: 2, background: `linear-gradient(90deg, transparent, ${TEAM_COLORS.sanJoseTeal}, transparent)` }} />
          <motion.h2
            style={{ textAlign: 'center', fontSize: isMobile ? 'clamp(1.8rem, 7vw, 2.5rem)' : 'clamp(2rem, 5vw, 3.5rem)', background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: isMobile ? 32 : 48, textTransform: 'uppercase', letterSpacing: isMobile ? 2 : 3, fontWeight: 700 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            2025–2026 tournament schedule
          </motion.h2>
          {loadingTournaments ? (
            <p style={{ textAlign: 'center', color: TEAM_COLORS.gladiatorGrey, fontSize: '1.1rem', margin: 0 }}>Loading schedule…</p>
          ) : tournaments.length > 0 ? (
            <div style={{ maxWidth: 1200, margin: '0 auto', overflowX: 'auto', borderRadius: 12, border: `2px solid ${TEAM_COLORS.sanJoseTeal}`, boxShadow: `0 10px 30px ${TEAM_COLORS.tealGlow}` }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem', background: '#1a1a1a' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '14px 18px', textAlign: 'left', background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, #004d54)`, color: '#fff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.9rem', borderBottom: `3px solid ${TEAM_COLORS.vegasGold}` }}>Tournament Name</th>
                    <th style={{ padding: '14px 18px', textAlign: 'left', background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, #004d54)`, color: '#fff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.9rem', borderBottom: `3px solid ${TEAM_COLORS.vegasGold}` }}>Location</th>
                    <th style={{ padding: '14px 18px', textAlign: 'left', background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, #004d54)`, color: '#fff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.9rem', borderBottom: `3px solid ${TEAM_COLORS.vegasGold}` }}>Dates</th>
                    <th style={{ padding: '14px 18px', textAlign: 'left', background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, #004d54)`, color: '#fff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.9rem', borderBottom: `3px solid ${TEAM_COLORS.vegasGold}` }}>Organizer</th>
                  </tr>
                </thead>
                <tbody>
                  {tournaments.map((row, idx) => (
                    <tr key={idx} style={{ transition: 'background 0.2s' }}>
                      <td style={{ padding: '14px 18px', color: '#f0f8ff', borderBottom: '1px solid rgba(0,109,117,0.2)' }} data-label="Tournament">{row.name}</td>
                      <td style={{ padding: '14px 18px', color: '#f0f8ff', borderBottom: '1px solid rgba(0,109,117,0.2)' }} data-label="Location">{row.location}</td>
                      <td style={{ padding: '14px 18px', color: '#f0f8ff', borderBottom: '1px solid rgba(0,109,117,0.2)' }} data-label="Dates">{row.dates}</td>
                      <td style={{ padding: '14px 18px', color: '#f0f8ff', borderBottom: '1px solid rgba(0,109,117,0.2)' }} data-label="Organizer">{row.organizer}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: TEAM_COLORS.gladiatorGrey, fontSize: '1.1rem', margin: 0 }}>Tournament schedule coming soon!</p>
          )}
        </section>

        {/* Final CTA */}
        <section style={{ padding: isMobile ? '60px 20px' : '80px 24px 100px', background: '#0a0a0a', textAlign: 'center' }}>
          <motion.h2
            style={{ fontSize: isMobile ? 'clamp(1.9rem, 6vw, 2.8rem)' : 'clamp(2rem, 5vw, 3rem)', fontWeight: 800, margin: '0 0 32px', textTransform: 'uppercase', letterSpacing: 2, background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            READY TO EARN THE JERSEY?
          </motion.h2>
          <motion.div
            style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 16 : 20, justifyContent: 'center', alignItems: 'center', maxWidth: 800, margin: '0 auto' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Link
              to="/tryouts"
              style={{
                display: 'inline-block',
                padding: isMobile ? '14px 32px' : '16px 40px',
                fontSize: isMobile ? '1.05rem' : '1.15rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 1.5,
                color: '#0a0a0a',
                background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`,
                borderRadius: 8,
                textDecoration: 'none',
                boxShadow: `0 8px 24px ${TEAM_COLORS.tealGlow}`,
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                width: isMobile ? '100%' : 'auto'
              }}
              onMouseOver={e => {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = `0 12px 32px ${TEAM_COLORS.goldGlow}`;
              }}
              onMouseOut={e => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 8px 24px ${TEAM_COLORS.tealGlow}`;
              }}
            >
              Register for 12U/14U Tryouts
            </Link>
            <a
              href="mailto:gcghockey@gmail.com"
              style={{
                display: 'inline-block',
                padding: isMobile ? '14px 32px' : '16px 40px',
                fontSize: isMobile ? '1.05rem' : '1.15rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 1.5,
                color: '#0a0a0a',
                background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`,
                borderRadius: 8,
                textDecoration: 'none',
                boxShadow: `0 8px 24px ${TEAM_COLORS.tealGlow}`,
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                width: isMobile ? '100%' : 'auto'
              }}
              onMouseOver={e => {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = `0 12px 32px ${TEAM_COLORS.goldGlow}`;
              }}
              onMouseOut={e => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 8px 24px ${TEAM_COLORS.tealGlow}`;
              }}
            >
              Contact a Coach
            </a>
          </motion.div>
        </section>

        <footer style={{ 
          background: 'linear-gradient(180deg, #0a0a0a, #000)',
          color: '#f0f8ff', 
          padding: isMobile ? '40px 15px 30px' : '60px 20px 40px', 
          textAlign: 'center',
          borderTop: isMobile ? `1px solid ${TEAM_COLORS.sanJoseTeal}` : `2px solid ${TEAM_COLORS.sanJoseTeal}`
        }}>
          <div style={{ 
            marginBottom: isMobile ? 15 : 20,
            display: 'flex',
            justifyContent: 'center',
            gap: isMobile ? 20 : 30,
            flexWrap: 'wrap'
          }}>
            <a 
              href="https://www.instagram.com/gcghockey" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ 
                color: TEAM_COLORS.sanJoseTeal, 
                textDecoration: 'none',
                fontSize: isMobile ? '1rem' : '1.1rem',
                fontWeight: 600,
                transition: 'all 0.3s ease',
                letterSpacing: 1
              }}
              onMouseOver={e => e.target.style.color = TEAM_COLORS.vegasGold}
              onMouseOut={e => e.target.style.color = TEAM_COLORS.sanJoseTeal}
            >
              Instagram
            </a>
            <a 
              href="https://www.facebook.com/profile.php?id=61559199442580" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ 
                color: TEAM_COLORS.sanJoseTeal, 
                textDecoration: 'none',
                fontSize: isMobile ? '1rem' : '1.1rem',
                fontWeight: 600,
                transition: 'all 0.3s ease',
                letterSpacing: 1
              }}
              onMouseOver={e => e.target.style.color = TEAM_COLORS.vegasGold}
              onMouseOut={e => e.target.style.color = TEAM_COLORS.sanJoseTeal}
            >
              Facebook
            </a>
          </div>
          <div style={{ marginTop: isMobile ? 20 : 30 }}>
            <Link 
              to="/tryouts"
              style={{ 
                display: 'inline-block',
                padding: isMobile ? '12px 30px' : '12px 35px',
                background: `linear-gradient(135deg, ${TEAM_COLORS.sanJoseTeal}, ${TEAM_COLORS.vegasGold})`,
                color: '#0a0a0a',
                fontWeight: 700,
                textDecoration: 'none',
                borderRadius: 4,
                fontSize: isMobile ? '1rem' : '1.1rem',
                textTransform: 'uppercase',
                letterSpacing: isMobile ? 1 : 2,
                boxShadow: `0 5px 20px ${TEAM_COLORS.tealGlow}`,
                transition: 'all 0.3s ease',
                touchAction: 'manipulation'
              }}
              onMouseOver={e => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 8px 25px ${TEAM_COLORS.tealGlow}`;
              }}
              onMouseOut={e => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 5px 20px ${TEAM_COLORS.tealGlow}`;
              }}
            >
              Join the Gladiators
            </Link>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default SplitScroll;